/**
 * Single source of truth for build/host configuration.
 *
 * Nothing else in the app may read `import.meta.env` directly — that keeps the
 * BlueHost/subdomain specifics in one auditable place.
 */

function str(value: unknown, fallback: string): string {
  const s = typeof value === 'string' ? value.trim() : ''
  return s.length > 0 ? s : fallback
}

function bool(value: unknown, fallback: boolean): boolean {
  if (typeof value !== 'string') return fallback
  const s = value.trim().toLowerCase()
  if (['1', 'true', 'yes', 'on'].includes(s)) return true
  if (['0', 'false', 'no', 'off'].includes(s)) return false
  return fallback
}

const rawBase = str(import.meta.env.VITE_BASE_PATH, '/')

export const env = {
  /** Always `/` or `/sub/folder/`. Mirrors `base` in vite.config.ts. */
  basePath: `/${rawBase.replace(/^\/+|\/+$/g, '')}/`.replace('//', '/'),
  /** Prefix every mock/real API call is written against. */
  apiBaseUrl: str(import.meta.env.VITE_API_BASE_URL, '/api/v1'),
  /** Absolute origin advertised in the Admin -> Public API tab. */
  publicOrigin: str(import.meta.env.VITE_PUBLIC_ORIGIN, 'https://services.mymdv.org'),
  /** When false, the client talks to a real backend over `fetch`. */
  useMockApi: bool(import.meta.env.VITE_USE_MOCK_API, true),
  /** Seeds a full demo org on first run. */
  seedDemoData: bool(import.meta.env.VITE_SEED_DEMO_DATA, true),
  isDev: import.meta.env.DEV,
} as const

/** `/plans` -> `/services/plans` when hosted in a sub-folder. */
export function withBase(path: string): string {
  return `${env.basePath}${path.replace(/^\/+/, '')}`
}

/** Fully-qualified URL for a public API path, for copy-to-clipboard in Admin. */
export function publicApiUrl(path: string): string {
  const suffix = `${env.apiBaseUrl}/public/${path.replace(/^\/+/, '')}`.replace(/\/{2,}/g, '/')
  return `${env.publicOrigin.replace(/\/+$/, '')}${suffix}`
}
